// ============================================================
// THE ACTOR STRIP'S SQUAD PART (Squad Mode stage 7, canvas board 8): the
// Oath's abilities as plates for the brother you act as - start one (its
// cost and action) or join one running on his stage (free) - each dimmed
// with its reason, never hidden (ruled: "squad plates stay, dimmed with the
// reason").
//
// Pure. Zero React dependency.
// ============================================================
import type { Combatant } from '../roster/types';
import type { Pool } from './pool';
import type { SquadAbility } from './tactics';

export interface AbilityPlate {
  id: string;
  name: string;
  cost: number;
  /** The action starting it takes (joining takes none). */
  action: SquadAbility['action'];
  /** Start it (its cost and action), or join it running (free). */
  kind: 'start' | 'join';
  /** The running ability's key, to join or to say he is in it. */
  key: string | null;
  /** He has it now. */
  pressed: boolean;
  /** Why it cannot be pressed; null when it can. */
  why: string | null;
}

interface PlateFacts {
  brother: Combatant;
  abilities: readonly SquadAbility[];
  pool: Pool | null;
  sceneId: string | null;
  /** The one-per-turn house rule. */
  limit: boolean;
  /** The GM may press past the limit (logged as an override), so it does not dim for him. */
  isGm: boolean;
}

function whyNot(f: PlateFacts, a: SquadAbility, joining: boolean, pressed: boolean): string | null {
  const ks = f.brother.state.killTeam;
  if (!ks?.on) return 'Enter Squad Mode first.';
  if (pressed) return 'He is in it.';
  if (f.limit && !f.isGm && ks.usedKey) return 'He has had a squad ability since his last turn.';
  if (!joining && (f.pool?.pool ?? 0) < a.cost) return `Not enough Cohesion: it costs ${a.cost}.`;
  return null;
}

/** The Oath's abilities as his plates, in the Tactics' order. */
export function abilityPlates(f: PlateFacts): AbilityPlate[] {
  const joined = new Set((f.brother.state.killTeam?.joined ?? []).map((j) => j.key));
  return f.abilities.map((a) => {
    const running = f.pool?.running.find((r) => r.abilityId === a.id && r.sceneId === f.sceneId) ?? null;
    const pressed = !!running && joined.has(running.key);
    const kind = running ? 'join' : 'start';
    return {
      id: a.id,
      name: a.name,
      cost: a.cost,
      action: a.action,
      kind,
      key: running?.key ?? null,
      pressed,
      why: whyNot(f, a, !!running, pressed),
    };
  });
}
